import * as yup from "yup";
import GenericButton from "../../../shared/ui/GenericButton";
import PageTitle from "../../../shared/ui/PageTitle";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import BoxContent from "../../../shared/ui/BoxContent";
import useCreateTest from "../../tests/create/hooks/useCreateTest";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import FormLabelBox from "../../../shared/ui/FormLabelBox";
import FormFieldBox from "../../../shared/ui/FormFieldBox";
import ErrorsList from "../../../shared/ui/ErrorsList";
import Grid from "@mui/material/Grid";
import {APIPostWorkPlan} from "../api/workPlan";
import {useAuth} from "../../../hooks/useAuth";
import {APIGetAllClassrooms} from "../../classrooms/api/classrooms";
import {getRomanNumber} from "../../../utils/functions";
import Typography from "@mui/material/Typography";
import Loading from "../../../shared/ui/Loading";
import FormField from "../../../shared/form/FormField";
import RadioField from "../../../shared/form/RadioField";
import StandardCategoriesListing from "../../tests/create/components/StandardsCategoriesListing";
import SchoolTrainingCategoriesListing from "../../tests/create/components/SchoolTrainingCategoriesListing";

export interface IFormWorkPlan {
    classroom: string;
    subject: string;
    grade: number;
    category: number;
}

const schema = yup.object({
    category: yup.string(),
    classroom: yup.string(),
});

const WorkPlanGenerate = () => {
    const {update} = useAuth();
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState("");
    const [classrooms, setClassrooms] = useState<any[]>([]);
    const [loadingClassrooms, setLoadingClassrooms] = useState<boolean>(true);

    const {
        loading,
        grades,
        subjects,
        createTestData,
        setSubjectHandler,
        setGradeHandler,
        setSimpleClassroomHandler,
        setSelectedCategoriesHandler,
        setSelectedStandardsHandler
    } = useCreateTest({type: "school", isWorkPlan: true});

    const methods = useForm<IFormWorkPlan>({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        setLoadingClassrooms(true);
        APIGetAllClassrooms()
            .then((response) => {
                if (response.data.success) {
                    // console.log("getClassroom OK", response);
                    const newClassrooms = response.data.data.items.map((item: { name: string; grade: number; id: string }) => {
                        return {
                            label: `(${getRomanNumber(item.grade)}) ${item.name}`,
                            value: item.id,
                            ...item,
                        };
                    });
                    setClassrooms(newClassrooms);

                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(() => {
                // console.log("getClassroom ERR", err);
                update("error", { show: true, code: "A001" });
            })
            .finally(() => {
                setLoadingClassrooms(false);
            })
    }, []);

    const setClassroomHandler = (event: any, value: any) => {
        methods.clearErrors("classroom");
        setSimpleClassroomHandler(event, value);
    }

    const onSubmit = () => {
        methods.clearErrors();
        setErrorMessage("");

        let classroom = createTestData.classroom?.value;

        if(
            classroom !== "" ||
            createTestData.subject.value !== "" ||
            createTestData.grade.value !== "" ||
            createTestData.category.value !== ""
        ) {
            update("loading", { show: true, modal: true });
            APIPostWorkPlan({
                classroom_id: classroom,
                subcategory_id:  createTestData.category.value,
            })
                .then((response) => {
                    if(response.data.success) {
                        update("error", {
                            show: true,
                            closable: true,
                            title: "Planul de lucru este în curs de generare.",
                            message: "Acesta va fi disponibil în secțiunea Plan de lucru a contului tău.",
                            icon: "fat face-smile",
                            button: "ÎNCHIDE",
                            returnFunction: () => {
                                navigate(-1);
                            },
                        });
                        update("loading", { show: false, modal: true });
                    } else {

                        update("error", { show: true, code: "A002" });
                    }
                })
                .catch((err) => {
                    // console.log("postWorkPlan ERR", err);
                    if (err.response.status === 400) {
                        let errors = err.response.data.data;
                        if (errors.subcategory_id) {
                            methods.setError("category", { type: "server", message: errors.subcategory_id[0] });
                        } else if (errors.classroom_id) {
                            methods.setError("classroom", { type: "server", message: errors.classroom_id[0] });
                        } else {
                            setErrorMessage(errors.value);
                        }
                        update("loading", { show: false });
                    } else {
                        update("error", { show: true, code: "A001" });
                    }
                });
        } else {
            setErrorMessage("Nu ați modificat niciun câmp.");
        }
    }

    return (
        <Box component="main">
            <PageTitle
                back={true}
                actions={
                    <GenericButton
                        type="close"
                        onClick={() => {
                            navigate(-1);
                        }}
                    />
                }>
                Generează plan de lucru
            </PageTitle>

            <Box width="100%">
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                        <Typography variant="h3" mb={3} mt={3}>
                            Alege clasa pentru care dorești generarea planului de lucru
                        </Typography>
                        {loadingClassrooms ? (
                            <Loading show={true}/>
                        ) : classrooms.length === 0 ? (
                            <Box>Nicio clasă introdusă în platformă.</Box>
                        ) : (<BoxContent>
                            <>
                                <FormField
                                    name="classroom"
                                    type="autocomplete-controlled"
                                    label="Alege clasa"
                                    multiple={false}
                                    options={classrooms}
                                    errorMessage={methods.formState.errors.classroom?.message}
                                    error={!!methods.formState.errors.classroom?.message}
                                    onChange={setClassroomHandler}
                                    disableClearable={true}
                                />
                            </>
                        </BoxContent>)}

                        <Typography variant="h3" mb={3} mt={3}>
                            Alege materia
                        </Typography>
                        <BoxContent>
                            <FormField
                                name="subject"
                                type="controlled-select"
                                label="Alege materia"
                                value={createTestData.subject.value}
                                onChange={setSubjectHandler}
                                options={subjects.map((item: { name: string; id: string }) => {
                                    return {
                                        label: item.name,
                                        value: item.id,
                                        ...item,
                                    };
                                })}
                                errorMessage={methods.formState.errors.subject?.message}
                                error={!!methods.formState.errors.subject?.message}
                            />
                        </BoxContent>

                        {createTestData.subject.value && (
                            <>
                                <Typography variant="h3" mb={3} mt={3}>
                                    Alege clasa testului
                                </Typography>

                                <BoxContent>
                                    {loading.loading && loading.name === "grade" ? (
                                        <Loading show={true}/>
                                    ) : (
                                        <RadioField
                                            row={true}
                                            name="grade"
                                            columns={true}
                                            defaultValue={createTestData.grade.value}
                                            onChange={setGradeHandler}
                                            options={grades.map((item: { name: string; id: string }) => {
                                                return {
                                                    label: item.name,
                                                    value: item.id.toString(),
                                                    ...item,
                                                };
                                            })}
                                            error={methods.formState.errors.grade?.message}
                                        />
                                    )}
                                </BoxContent>
                            </>
                        )}

                        {createTestData.grade.value && (
                            <>
                                <Typography variant="h3" mb={3} mt={3}>
                                    Alege capitolul
                                </Typography>

                                {loading.loading && loading.name === "categories" ? (
                                    <Loading show={true}/>
                                ) : (
                                    <>
                                        {createTestData.grade.value <= 8 ? (
                                            <StandardCategoriesListing
                                                isWorkPlan={true}
                                                categories={createTestData.categories}
                                                setSelectedStandardsHandler={setSelectedStandardsHandler}
                                            />
                                        ) : (
                                            <BoxContent>
                                                <SchoolTrainingCategoriesListing
                                                    categories={createTestData.categories}
                                                    type="training"
                                                    setSelectedCategoriesHandler={setSelectedCategoriesHandler}
                                                />
                                            </BoxContent>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </form>
                </FormProvider>

                <Grid container spacing={1.5} alignItems="center">
                    <FormLabelBox />
                    <FormFieldBox>
                        <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                    </FormFieldBox>
                </Grid>
            </Box>
        </Box>
    )
}

export default WorkPlanGenerate;